export type AnnouncePriority = 'polite' | 'assertive';

export interface Announcer {
  announce: (message: string, priority?: AnnouncePriority) => void;
  destroy: () => void;
}

export function createAnnouncer(): Announcer {
  if (typeof document === 'undefined') {
    return { announce: () => {}, destroy: () => {} };
  }

  const make = (priority: AnnouncePriority): HTMLDivElement => {
    const el = document.createElement('div');
    el.setAttribute('aria-live', priority);
    el.setAttribute('aria-atomic', 'true');
    el.setAttribute('role', priority === 'assertive' ? 'alert' : 'status');
    el.style.position = 'absolute';
    el.style.width = '1px';
    el.style.height = '1px';
    el.style.padding = '0';
    el.style.margin = '-1px';
    el.style.overflow = 'hidden';
    el.style.clip = 'rect(0,0,0,0)';
    el.style.whiteSpace = 'nowrap';
    el.style.border = '0';
    document.body.appendChild(el);
    return el;
  };

  const politeRegion = make('polite');
  const assertiveRegion = make('assertive');
  let lastMessage = '';
  let clearTimer: ReturnType<typeof setTimeout> | null = null;

  return {
    announce(message, priority = 'polite') {
      const target = priority === 'assertive' ? assertiveRegion : politeRegion;
      if (message === lastMessage) {
        target.textContent = '';
        requestAnimationFrame(() => { target.textContent = message; });
      } else {
        target.textContent = message;
      }
      lastMessage = message;
      if (clearTimer) clearTimeout(clearTimer);
      clearTimer = setTimeout(() => { target.textContent = ''; }, 5000);
    },
    destroy() {
      if (clearTimer) clearTimeout(clearTimer);
      politeRegion.remove();
      assertiveRegion.remove();
    }
  };
}
